import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "ds_legacy/components/Tooltip";
import { GREY_500 } from "ds_legacy/materials/colors";
import { margin, sizing } from "ds_legacy/materials/metrics";
import { FONT_SIZE_16, LongMenuText } from "ds_legacy/materials/typography";
import { Menu } from "dsl/atoms/Menu";
import { useDropdown } from "dsl/hooks";
import { kebabCase } from "lodash";
import { MoreVerticalIcon } from "lucide-react";
import { MouseEvent, MouseEventHandler, ReactElement } from "react";
import styled from "styled-components";
import { useTranslations } from "translations";

export type LongMenuOption = {
  disabled?: boolean;
  icon?: ReactElement<any>;
  label: string;
  onClick: MouseEventHandler;
  tooltip?: string;
};

export type LongMenuProps = {
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  options: LongMenuOption[];
  size?: "large" | "medium" | "small";
  testId?: string;
};

const LONG_MENU_ITEM_HEIGHT = 48;

const StyledMenu = styled(Menu)`
  margin-top: ${margin(6)};
`;

// Allow displaying tooltips on disabled menu items
const StyledMenuItem = styled(MenuItem)`
  &&.Mui-disabled {
    pointer-events: inherit;
    cursor: inherit;
  }
  &&.Mui-disabled:active {
    pointer-events: none;
    cursor: default;
  }
`;

const LONG_MENU_ID = "long-button";

function LongMenu({
  onClick: onMenuClick,
  options,
  size = "large",
  testId,
}: LongMenuProps) {
  const { anchor, onClose, onOpen, open } = useDropdown();
  const translations = useTranslations();

  return (
    <div>
      <IconButton
        aria-controls={open ? "long-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        aria-label={translations.menu.more}
        data-testid={testId}
        id={LONG_MENU_ID}
        size={size}
        onClick={(e: MouseEvent<HTMLButtonElement>) => {
          onMenuClick?.(e);
          onOpen(e);
        }}
      >
        <MoreVerticalIcon size={FONT_SIZE_16} />
      </IconButton>
      <StyledMenu
        anchorEl={anchor}
        anchorOrigin={{
          horizontal: "left",
          vertical: "top",
        }}
        id="long-menu"
        MenuListProps={{
          "aria-labelledby": LONG_MENU_ID,
        }}
        open={open}
        slotProps={{
          paper: {
            style: {
              maxHeight: LONG_MENU_ITEM_HEIGHT * 4.5,
              minWidth: sizing(20),
            },
          },
        }}
        onClose={onClose}
      >
        {options.map(({ disabled, icon, label, onClick, tooltip }, index) => (
          <Tooltip key={index} title={tooltip}>
            <StyledMenuItem
              data-testid={`${testId}-${kebabCase(label)}`}
              dense
              disabled={disabled}
              tabIndex={index}
              onClick={(event) => {
                onClick(event);
                onClose();
              }}
            >
              {icon && (
                <ListItemIcon
                  style={{
                    color: GREY_500,
                    marginRight: margin(1),
                    minWidth: "max-content",
                  }}
                >
                  {icon}
                </ListItemIcon>
              )}
              <LongMenuText>{label}</LongMenuText>
            </StyledMenuItem>
          </Tooltip>
        ))}
      </StyledMenu>
    </div>
  );
}
export default LongMenu;
